import {getUserLocale} from '@Utils/locale.util';

const numberFormatter = new Intl.NumberFormat(getUserLocale());

export const formatNumber = (toFormat: number): string => {
  return numberFormatter.format(toFormat);
};

export const formatNumberInUSD = (toFormat: number): string => {
  return window.PMW.util.currencyFormat(toFormat, window.PMW.CURRENCY_CODE_USD);
};

export const formatWholeNumberInUSD = (value: number): string => {
  return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0}).format(value);
};

/**
 * A USD amount showing cents only when it has them: `10 → "$10"`, `10.5 → "$10.50"`. For copy that quotes a
 * configured limit, where `formatWholeNumberInUSD` would round a $10.50 threshold to "$11" and state a figure the
 * code does not enforce.
 */
export const formatExactNumberInUSD = (value: number): string => {
  const fractionDigits = Number.isInteger(value) ? 0 : 2;
  return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits}).format(value);
};

export const formatNumberInCurrency = (toFormat: number, currencyCode: string): string => {
  return window.PMW.util.currencyFormat(toFormat, currencyCode);
};

const PLAN_PRICE_FRACTION_DIGITS = 2;

/**
 * Prices as the pricing page prints them: cents appear only on an amount that has them. Intl's currency style pads
 * every whole price to its currency's default precision ("US$ 13.00"), which the plan surfaces do not want. Built
 * from the shared formatter so only the fraction digits differ from every other price in the app.
 */
const getPlanPriceFormatter = (toFormat: number, currencyCode: string): Intl.NumberFormat => {
  const {maximumFractionDigits = PLAN_PRICE_FRACTION_DIGITS} = window.PMW.util.getCurrencyFormatter(currencyCode).resolvedOptions();
  const minimumFractionDigits = Number.isInteger(toFormat) ? 0 : Math.min(PLAN_PRICE_FRACTION_DIGITS, maximumFractionDigits);
  return new Intl.NumberFormat(navigator.language, {style: 'currency', currency: currencyCode, maximumFractionDigits, minimumFractionDigits});
};

export const formatPlanPriceInCurrency = (toFormat: number, currencyCode: string): string => {
  return getPlanPriceFormatter(toFormat, currencyCode).format(toFormat);
};

export const getRandomNumberInRange = (min: number, max: number): number => {
  return Math.random() * (max - min) + min;
};

export const formatToNearestThousand = (val: number): string => {
  if (val > 999) {
    const temp = val / 1000;
    return `${temp.toFixed(0)}k`;
  }

  return String(val);
};

export const formatToThousand = (val: number): string => {
  if (Math.abs(val) > 999) {
    const temp = Math.floor(val / 100) / 10;
    return `${temp}k`;
  }

  return String(val);
};

export const formatToMillion = (val: number): string => {
  if (Math.abs(val) > 999999) {
    const temp = Math.floor(val / 100000) / 10;
    return `${temp}M`;
  }

  return String(val);
};

export const abbreviateNumber = (val: number): string => {
  const num = Math.abs(val);
  if (num > 999999) {
    return formatToMillion(val);
  }

  if (num > 999) {
    return formatToThousand(val);
  }

  return String(val);
}

export interface FormattedCurrencyParts {
  currencySymbol: string;
  /** Whatever the locale puts between the symbol and the digits: nothing in en-US ("$9.95"), a space in fr-FR ("9,95 €"). */
  currencySymbolSpacing: string;
  amount: string;
  isCurrencySymbolFirst: boolean;
}

const EMPTY_CURRENCY_PARTS: FormattedCurrencyParts = {currencySymbol: '', currencySymbolSpacing: '', amount: '', isCurrencySymbolFirst: true};

/**
 * Splits a price the locale formatter has already produced into its symbol and digits so the two can be typeset at
 * different sizes. The spacing the locale itself puts between them is returned rather than discarded, so rendering the
 * pieces back to back reproduces the formatter's own string.
 */
export const getFormattedCurrencyParts = (toFormat: number, currencyCode: string): FormattedCurrencyParts => {
  const parts: Intl.NumberFormatPart[] = getPlanPriceFormatter(toFormat, currencyCode).formatToParts(toFormat);
  const currencyPartIndex = parts.findIndex((part: Intl.NumberFormatPart) => part.type === 'currency');
  if (currencyPartIndex === -1) {
    return {...EMPTY_CURRENCY_PARTS, amount: parts.map((part: Intl.NumberFormatPart) => part.value).join('')};
  }

  const isCurrencySymbolFirst = currencyPartIndex < parts.findIndex((part: Intl.NumberFormatPart) => part.type === 'integer');
  const spacingIndex = isCurrencySymbolFirst ? currencyPartIndex + 1 : currencyPartIndex - 1;
  const hasSpacing = parts[spacingIndex]?.type === 'literal';

  return {
    currencySymbol: parts[currencyPartIndex].value,
    currencySymbolSpacing: hasSpacing ? parts[spacingIndex].value : '',
    amount: parts
      .filter((part: Intl.NumberFormatPart, index: number) => part.type !== 'currency' && !(hasSpacing && index === spacingIndex))
      .map((part: Intl.NumberFormatPart) => part.value)
      .join(''),
    isCurrencySymbolFirst,
  };
};
